// src/components/Settings/Sidebar/Sidebar.tsx
import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  IconButton,
  Typography,
} from "@mui/material";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";

import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import FaceOutlinedIcon from "@mui/icons-material/FaceOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import TopicOutlinedIcon from "@mui/icons-material/TopicOutlined";

interface SidebarProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  activeTab: string | null;
  setActiveTab: (tab: string) => void;
  handleBack: () => void;
  handleNavigate: (path: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  sidebarOpen,
  toggleSidebar,
  activeTab,
  setActiveTab,
  handleBack,
  handleNavigate,
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100lvh"
      sx={{
        width: sidebarOpen ? 240 : 0,
        transition: "width 0.3s ease",
        height: "100lvh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
      >
        <IconButton onClick={toggleSidebar}>
          <MenuOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      {sidebarOpen && (
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              overflow: "auto",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <List>
              <ListItemButton
                onClick={handleBack}
                sx={{
                  marginBottom: 2,
                }}
              >
                <ChevronLeftOutlinedIcon
                  sx={{ mr: 1, fontSize: 16, color: "primary.main" }}
                />
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "primary.main",
                    fontWeight: 600,
                  }}
                >
                  กลับไปยังห้องสนทนา
                </Typography>
              </ListItemButton>
              <ListItem>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "text.primary",
                  }}
                >
                  จัดการระบบ
                </Typography>
              </ListItem>
              <ListItemButton
                selected={activeTab === "home"}
                onClick={() => handleNavigate("/settings")}
                sx={{
                  bgcolor: activeTab === "home" ? "primary.light" : "inherit",
                  marginBottom: "4px",
                }}
              >
                <AccountCircleOutlinedIcon
                  sx={{ mr: 1, fontSize: 16, color: "primary.main" }}
                />
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: activeTab === "home" ? "600" : "inherit",
                  }}
                >
                  หน้าแรก
                </Typography>
              </ListItemButton>
            </List>
            <ListItem>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "text.primary",
                }}
              >
                ตั้งค่าทั่วไป
              </Typography>
            </ListItem>
            <List>
              <ListItemButton
                selected={activeTab === "theme"}
                onClick={() => handleNavigate("/settings/theme")}
                sx={{
                  bgcolor: activeTab === "theme" ? "primary.light" : "inherit",
                  marginBottom: "4px",
                }}
              >
                <ColorLensOutlinedIcon
                  sx={{ mr: 1, fontSize: 16, color: "primary.main" }}
                />
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: activeTab === "theme" ? "600" : "inherit",
                  }}
                >
                  ตั้งค่าธีม
                </Typography>
              </ListItemButton>
              <ListItemButton
                selected={activeTab === "assistance"}
                onClick={() => handleNavigate("/settings/assistance")}
                sx={{
                  bgcolor:
                    activeTab === "assistance" ? "primary.light" : "inherit",
                  marginBottom: "4px",
                }}
              >
                <FaceOutlinedIcon
                  sx={{ mr: 1, fontSize: 16, color: "primary.main" }}
                />
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: activeTab === "assistance" ? "600" : "inherit",
                  }}
                >
                  ตั้งค่าบทบาทผู้ช่วย
                </Typography>
              </ListItemButton>
            </List>
            <ListItem>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "text.primary",
                }}
              >
                สำหรับผู้ดูแลระบบ
              </Typography>
            </ListItem>
            <List>
              <ListItemButton
                selected={activeTab === "users"}
                onClick={() => handleNavigate("/settings/users")}
                sx={{
                  bgcolor: activeTab === "users" ? "primary.light" : "inherit",
                  marginBottom: "4px",
                }}
              >
                <AdminPanelSettingsOutlinedIcon
                  color="warning"
                  sx={{ mr: 1, fontSize: 16 }}
                />
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: activeTab === "users" ? "600" : "inherit",
                  }}
                >
                  ข้อมูลผู้ใช้งาน
                </Typography>
              </ListItemButton>
              <ListItemButton
                selected={activeTab === "documents"}
                onClick={() => handleNavigate("/settings/documents")}
                sx={{
                  bgcolor:
                    activeTab === "documents" ? "primary.light" : "inherit",
                  marginBottom: "4px",
                }}
              >
                <TopicOutlinedIcon
                  color="warning"
                  sx={{ mr: 1, fontSize: 16 }}
                />
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: activeTab === "documents" ? "600" : "inherit",
                  }}
                >
                  ข้อมูลเอกสาร
                </Typography>
              </ListItemButton>
            </List>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Sidebar;
